import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { extractIntakeReviewParties } from "@/lib/ops/intakeReviewParties";

const SALE_PATH_LABELS: Record<string, string> = {
  cash: "Cash",
  rto: "RTO",
  finance: "Finance",
  inventory_no_sale_yet: "Stock",
};

export type UnitHandoff = {
  display_label?: string | null;
  serial?: string | null;
  customer_name?: string | null;
  seller_location_name?: string | null;
  deal_display_name?: string | null;
  sale_path?: string | null;
  created_at?: string | null;
  build_completed_at?: string | null;
};

function text(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function record(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  return null;
}

/** Short age for a queue row. `now` is injectable so tests stay stable. */
export function handoffAgeLabel(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  const minutes = Math.max(0, Math.round((now - at) / 60000));
  if (minutes < 60) return minutes <= 1 ? "just now" : `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

export function salePathLabel(salePath: string | null | undefined): string | null {
  if (!salePath) return null;
  return SALE_PATH_LABELS[salePath] ?? null;
}

/** Size and style already stored on the intake. Skips ids. */
export function intakeBuildingLabel(row: Pick<OpsOrderIntakeRow, "proposed_options" | "raw_payload">): string {
  const opt = row.proposed_options ?? {};
  const width = text(opt.width);
  const length = text(opt.length);
  const size = text(opt.size) || (width && length ? `${width}x${length}` : "");
  const style = text(opt.style) || text(opt.model);
  const fromOptions = [size, style].filter(Boolean).join(" ");
  if (fromOptions) return fromOptions;

  const raw = row.raw_payload ?? {};
  const direct = text(raw.size) || text(raw.building_size) || text(raw.product_interest);
  if (direct) return direct;

  const order = record(raw.order) ?? raw;
  const props = record(order.integrationProperties);
  const building = props ? record(props.buildingSize) : null;
  const pair = building ? [text(building.width), text(building.length)].filter(Boolean).join("x") : "";
  const buildingStyle = props ? text(props.buildingStyle) : "";
  return [pair, buildingStyle].filter(Boolean).join(" ");
}

export function intakeHandoffTitle(row: OpsOrderIntakeRow): string {
  const name = extractIntakeReviewParties(row).customer?.name?.trim() ?? "";
  if (name) return name;
  if (row.kind === "inventory") return "Stock";
  return "Customer";
}

export function unitHandoffTitle(unit: UnitHandoff): string {
  const customer = unit.customer_name?.trim() ?? "";
  if (customer) return customer;
  const deal = unit.deal_display_name?.trim() ?? "";
  if (deal && !deal.startsWith("pre-build:")) return deal;
  if (unit.sale_path === "inventory_no_sale_yet") return "Stock";
  return "Building";
}

/** Assigned serial, or a display label that is already a serial. Never `pre-build:`. */
export function unitHandoffSerial(unit: UnitHandoff): string | null {
  const serial = unit.serial?.trim() ?? "";
  if (serial) return serial;
  const label = unit.display_label?.trim() ?? "";
  if (label && !label.startsWith("pre-build:")) return label;
  return null;
}

export function unitHandoffOptionLabel(unit: UnitHandoff): string {
  const title = unitHandoffTitle(unit);
  const serial = unitHandoffSerial(unit);
  const place = unit.seller_location_name?.trim() ?? "";
  const plant = unit.build_completed_at ? "Plant finished" : "";
  const parts = [title];
  if (serial && serial !== title) parts.push(serial);
  else if (!serial) parts.push("No serial yet");
  if (place) parts.push(place);
  if (plant) parts.push(plant);
  return parts.join(" · ");
}
